import Image from 'next/image';
import helpfulIcon from './img/helpful.svg';
import notHelpfulIcon from './img/not-helpful.svg';
import feedbackIcon from './img/feedback.svg';
import { useRouter } from 'next/router';
import { analytics } from 'pages';
import { FormEvent, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

// I know this function isn't written the way that others are in the current codebase
// and isn't using chakra etc, it was done this way in isolation to get it done quickly and
// cleanly since I'm not super familiar with chakra and this whole page will likely be refactored
// soon anyway. Sorry!
export default function FeedbackModule() {
  const router = useRouter();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [activeBtn, setActiveBtn] = useState('');
  const [formComplete, setFormComplete] = useState(false);

  function expand(feedback: string) {
    setActiveBtn(feedback);

    if (feedback !== 'feedback') {
      analytics.track('Docs_Feedback_Sentiment Shared', {
        surface: 'Docs',
        location: 'Feedback',
        page: router.asPath ,
        sentimentShared: feedback
      })
    }
  }

  useEffect(() => {
    if (activeBtn && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [activeBtn]);

  function handleForm(e: FormEvent) {
    e.preventDefault();

    analytics.track('Docs_Feedback_Survey Submitted', {
      surface: 'Docs',
      location: 'Feedback',
      page: router.asPath ,
      sentimentShared: (e.currentTarget as HTMLElement).dataset.sentiment,
      feedbackProvided: e.currentTarget.querySelector('textarea')?.value
    })

    fetch('/api/feedback', {
      method: 'post',
      body: JSON.stringify({
        sentiment: (e.currentTarget as HTMLElement).dataset.sentiment,
        text: e.currentTarget.querySelector('textarea')?.value,
        page: router.asPath
      })
    });

    setFormComplete(true);
  }

  return (
    <div className={clsx('flex w-full flex-col text-sm sm:w-auto lg:ml-4')}>
      <p className={clsx('mb-3 mt-2 font-medium')}>
        What did you think of this content?
      </p>

      {formComplete ? (
        <p className="text-black/60 dark:text-white/60">
          Thank you so much for your feedback 🙇‍♂️
        </p>
      ) : (
        [
          { slug: 'helpful', icon: helpfulIcon, text: 'It was helpful' },
          {
            slug: 'not-helpful',
            icon: notHelpfulIcon,
            text: 'It was not helpful'
          },
          { slug: 'feedback', icon: feedbackIcon, text: 'I have feedback' }
        ].map(({ slug, icon, text }) => {
          const isActive = activeBtn === slug;
          const Element = isActive ? 'div' : 'button';

          return (
            <Element
              key={slug}
              className={clsx(
                'group rounded-md px-3 py-2 text-black/60 outline-2 outline-offset-1 outline-purple-500 ring-1 ring-transparent transition-all focus-visible:outline',
                'hover:bg-gray-100/[0.1] hover:shadow-md hover:ring-gray-950/[0.05]',
                'dark:text-white/60 dark:hover:bg-gray-400/[0.05] dark:hover:ring-gray-100/[0.1]',
                isActive && [
                  'w-full sm:w-[450px]',
                  'bg-gray-100/[0.1] data-[active=true]:shadow-lg data-[active=true]:ring-gray-950/[0.05]',
                  'dark:bg-gray-400/[0.05] dark:ring-gray-100/[0.1]'
                ],
                activeBtn.length > 0 && !isActive && 'hidden'
              )}
              data-active={isActive ? 'true' : undefined}
              onClick={isActive ? undefined : expand.bind(null, slug)}
            >
              <span className="flex items-center">
                <Image
                  src={icon}
                  alt=""
                  className={clsx(
                    'mr-2 group-hover:grayscale-0',
                    !isActive && 'grayscale-[90%]'
                  )}
                />
                {text}
              </span>
              {activeBtn === slug && (
                <form
                  onSubmit={handleForm}
                  className="mt-3 flex w-full flex-col gap-3"
                  data-sentiment={slug}
                >
                  <textarea
                    ref={textareaRef}
                    name="feedback"
                    className={clsx(
                      'min-h-[110px] w-full rounded border-none p-3 text-black/60 outline-2 outline-offset-2 outline-purple-500 ring-1 ring-gray-100 focus-visible:outline',
                      'dark:text-white/60 dark:ring-gray-100/30'
                    )}
                    placeholder="We'd love to hear your feedback!"
                  ></textarea>
                  <input
                    type="submit"
                    className="cursor-pointer self-end rounded-md bg-purple-500 px-3 py-1.5 text-sm font-semibold tracking-tight text-white outline-2 outline-offset-1 outline-purple-500 focus-visible:outline"
                    value="Send"
                  />
                </form>
              )}
            </Element>
          );
        })
      )}
    </div>
  );
}
